import React from "react";
import { LAYER_COLORS } from "./api";

const pts = (arr) => (arr || []).map((p) => p.join(",")).join(" ");

const strokeOf = (m) => {
  // DRW-8 — a takeoff mark is painted from the symbol registry, never from its own style
  if (m.symbol) return m.symbol.unmapped ? "#737373" : m.symbol.color;
  const s = m.style?.stroke;
  if (Array.isArray(s) && s.length >= 3) return `rgb(${s.slice(0, 3).map((v) => Math.round(v * 255)).join(",")})`;
  if (typeof s === "string") return s;
  return LAYER_COLORS[m.layer] || "#b91c1c";
};

const center = (g) => {
  if (g.x != null && g.w == null) return [g.x, g.y];
  if (g.rect) return [(g.rect[0] + g.rect[2]) / 2, (g.rect[1] + g.rect[3]) / 2];
  const ps = g.points || (g.strokes || []).flat();
  if (ps?.length) return [ps.reduce((a, p) => a + p[0], 0) / ps.length, ps.reduce((a, p) => a + p[1], 0) / ps.length];
  return [0, 0];
};

/** A cloud is a polygon drawn with scallops; enough to read as a Bluebeam revision cloud. */
function cloudPath(points) {
  if (!points || points.length < 2) return "";
  let d = "";
  const n = points.length;
  for (let i = 0; i < n; i += 1) {
    const [x1, y1] = points[i];
    const [x2, y2] = points[(i + 1) % n];
    const len = Math.hypot(x2 - x1, y2 - y1);
    const steps = Math.max(1, Math.round(len / 14));
    for (let s = 0; s < steps; s += 1) {
      const ax = x1 + ((x2 - x1) * s) / steps;
      const ay = y1 + ((y2 - y1) * s) / steps;
      const bx = x1 + ((x2 - x1) * (s + 1)) / steps;
      const by = y1 + ((y2 - y1) * (s + 1)) / steps;
      d += `${d ? "" : `M${ax},${ay} `}A7,7 0 0 1 ${bx},${by} `;
    }
  }
  return `${d}Z`;
}

function Pin({ x, y, k, spin = 0, color, label, icon, selected, onClick, testId }) {  return (
    <g transform={`translate(${x} ${y}) scale(${k}) rotate(${-spin})`} onClick={onClick} style={{ cursor: "pointer" }}
       data-testid={testId}>
      <path d="M0 0 L-9 -14 A11 11 0 1 1 9 -14 Z" fill={color} stroke={selected ? "#111" : "white"} strokeWidth={selected ? 2.5 : 1.5} />
      <circle cx="0" cy="-17" r="6" fill="white" />
      <text x="0" y="-14" textAnchor="middle" fontSize="8" fontWeight="700" fill="#111">{label ?? icon}</text>
    </g>
  );
}

const ISSUE_SUBJECTS = ["field issue", "rfi", "verify"];
const isIssueMarkup = (m) => ["punch_pin", "sticky"].includes(m.type)
  || ISSUE_SUBJECTS.includes(String(m.subject || "").toLowerCase());

/** FT-1 — installed reads as a checkmark and a solid green edge, not a faint shade. Only an
 *  issue-type markup keeps the old resolved look. */
function DoneMark({ x, y, k, spin = 0 }) {
  return (
    <g transform={`translate(${x} ${y}) scale(${k}) rotate(${-spin})`} pointerEvents="none" data-testid="installed-check">
      <circle cx="0" cy="0" r="9" fill="#15803d" stroke="white" strokeWidth="2" />
      <path d="M-4.5 0 L-1 3.5 L4.5 -3.5" fill="none" stroke="white" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </g>
  );
}

/** DRW-5 §6 — installed = an X through the box, in document coordinates (it scales with the
 *  paper, never floats), on a white halo so it reads over linework. */
function XMark({ box, k }) {
  const [x0, y0, x1, y1] = box;
  const size = Math.max(Math.min(x1 - x0, y1 - y0), 8);
  const w = Math.max(size * 0.09, 1.6 * k);
  return (
    <g pointerEvents="none" data-testid="installed-x">
      <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="white" strokeWidth={w * 2.2} strokeLinecap="round" />
      <line x1={x1} y1={y0} x2={x0} y2={y1} stroke="white" strokeWidth={w * 2.2} strokeLinecap="round" />
      <line x1={x0} y1={y0} x2={x1} y2={y1} stroke="#15803d" strokeWidth={w} strokeLinecap="round" />
      <line x1={x1} y1={y0} x2={x0} y2={y1} stroke="#15803d" strokeWidth={w} strokeLinecap="round" />
    </g>
  );
}

/** DRW-5 §6/§7 — partial / unresolved: the lower-right half filled diagonally plus the count. */
function HalfFill({ box, k, spin, text, tone = "#f59e0b", opacity = 0.35, corner = false }) {
  const [x0, y0, x1, y1] = box;
  const lx = corner ? x1 - 4 * k : (x0 + x1) / 2;
  const ly = corner ? y1 - 10 * k : (y0 + y1) / 2;
  return (
    <g pointerEvents="none" data-testid="partial-fill">
      <polygon points={`${x1},${y0} ${x1},${y1} ${x0},${y1}`} fill={tone} fillOpacity={opacity} />
      <line x1={x1} y1={y0} x2={x0} y2={y1} stroke={tone} strokeWidth={1.5 * k} />
      {text && (
        <g transform={`translate(${lx} ${ly}) scale(${k}) rotate(${-spin})`}>
          <rect x={corner ? -text.length * 5.2 - 6 : -text.length * 2.6 - 3} y="-7" width={text.length * 5.2 + 6} height="13" rx="3" fill="white" stroke={tone} strokeWidth="1" />
          <text textAnchor={corner ? "end" : "middle"} x={corner ? -3 : 0} y="3.5" fontSize="8" fontWeight="700" fill="#92400e">{text}</text>
        </g>
      )}
    </g>
  );
}

const bboxOf = (g) => {
  if (g.rect) return g.rect;
  const ps = g.points || (g.strokes || []).flat();
  if (!ps?.length) return [g.x - 8, g.y - 8, g.x + 8, g.y + 8];
  return [Math.min(...ps.map((p) => p[0])), Math.min(...ps.map((p) => p[1])), Math.max(...ps.map((p) => p[0])), Math.max(...ps.map((p) => p[1]))];
};
/** §4 — a takeoff box grows a touch around its centroid so a finger lands inside it. */
const grow = (points, f = 1.08, pad = 2) => {
  const [cx, cy] = center({ points });
  return points.map(([x, y]) => [cx + (x - cx) * f + Math.sign(x - cx) * pad, cy + (y - cy) * f + Math.sign(y - cy) * pad]);
};
/** PLAN-COMPLETE §12 — a mark that is not linked to an FRS record cannot be completed from the
 *  plan, and says so on the paper rather than looking like an ordinary open mark. */
function UnlinkedGlyph({ box, k, spin }) {
  const [x0, y0, x1, y1] = box;
  const pad = 2 * k;
  return (
    <g pointerEvents="none" data-testid="unlinked-glyph">
      <rect x={x0 - pad} y={y0 - pad} width={x1 - x0 + pad * 2} height={y1 - y0 + pad * 2}
            fill="rgba(225,29,72,0.10)" stroke="#e11d48" strokeWidth={1.8 * k} strokeDasharray={`${3 * k} ${2.5 * k}`} />
      <g transform={`translate(${x1} ${y0}) scale(${k}) rotate(${-spin})`}>
        <circle cx="0" cy="0" r="6" fill="#e11d48" stroke="white" strokeWidth="1.4" />
        <text x="0" y="3" textAnchor="middle" fontSize="9" fontWeight="700" fill="white">?</text>
      </g>
    </g>
  );
}

/** PLAN-COMPLETE §4 — a tapped mark reads as selected until it is saved or cleared. */
function TapMark({ box, k }) {
  const [x0, y0, x1, y1] = box;
  const pad = 3 * k;
  return (
    <g pointerEvents="none" data-testid="tapped-mark">
      <rect x={x0 - pad} y={y0 - pad} width={x1 - x0 + pad * 2} height={y1 - y0 + pad * 2}
            fill="rgba(21,128,61,0.18)" stroke="#15803d" strokeWidth={2.5 * k} strokeDasharray={`${5 * k} ${3 * k}`} />
      <circle cx={x1} cy={y0} r={4 * k} fill="#15803d" stroke="white" strokeWidth={1.2 * k} />
    </g>
  );
}

/** Corner handles on a selected shape — the visible cue that it can be resized (the resize itself
 *  is a tap on the opposite corner, which works with a glove on). */
function Handles({ g, k }) {
  const pts2 = g.rect ? [[g.rect[0], g.rect[1]], [g.rect[2], g.rect[1]], [g.rect[2], g.rect[3]], [g.rect[0], g.rect[3]]]
    : (g.points || []);
  if (!pts2.length) return null;
  return (
    <g pointerEvents="none" data-testid="shape-handles">
      {pts2.map(([x, y], i) => <rect key={i} x={x - 4 * k} y={y - 4 * k} width={8 * k} height={8 * k} fill="white" stroke="#111" strokeWidth={1.2 * k} />)}
    </g>
  );
}

function renderMarkup(m, { k, spin, hl, selectedId, onSelect }) {
    const g = m.geometry || {};
    const sel = m.id === selectedId;
    const hot = hl.has(m.id);
    const color = strokeOf(m);
    const w = (m.style?.width || 1.5);
    const common = { onClick: (e) => { e.stopPropagation(); onSelect?.(m, e); }, style: { cursor: "pointer" },
      "data-testid": `markup-${m.id}` };
    const verify = m.verify_state === "verify";
    const installed = m.status === "resolved" && !isIssueMarkup(m);
    const dim = (m.status === "resolved" && isIssueMarkup(m)) ? 0.45 : (hl.size && !hot) ? 0.18 : 1;   // DRW-8 — a legend row dims the rest
    switch (m.type) {
      case "pen":
      case "highlighter":
        return (
          <g key={m.id} {...common} opacity={dim}>
            {(g.strokes || []).map((s, i) => (
              <polyline key={i} points={pts(s)} fill="none" stroke={m.type === "highlighter" ? (m.style?.stroke || "#facc15") : color}
                        strokeWidth={m.type === "highlighter" ? Math.max(w, 10) : w} strokeOpacity={m.type === "highlighter" ? 0.4 : 1}
                        strokeLinecap="round" strokeLinejoin="round" vectorEffect={m.type === "highlighter" ? undefined : "non-scaling-stroke"} />
            ))}
            {sel && <polyline points={pts((g.strokes || [])[0] || [])} fill="none" stroke="#111" strokeDasharray="4 3" strokeWidth={1} vectorEffect="non-scaling-stroke" />}
          </g>
        );
      case "arrow":
      case "line": {
        const p = g.points || [];
        if (p.length < 2) return null;
        const [x1, y1] = p[0]; const [x2, y2] = p[p.length - 1];
        const ang = Math.atan2(y2 - y1, x2 - x1);
        const ah = 10 * k;
        return (
          <g key={m.id} {...common} opacity={dim}>
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={sel ? "#111" : color} strokeWidth={w * 1.5} vectorEffect="non-scaling-stroke" />
            <line x1={x1} y1={y1} x2={x2} y2={y2} stroke="transparent" strokeWidth={14 * k} />
            {m.type === "arrow" && (
              <polygon points={`${x2},${y2} ${x2 - ah * Math.cos(ang - 0.4)},${y2 - ah * Math.sin(ang - 0.4)} ${x2 - ah * Math.cos(ang + 0.4)},${y2 - ah * Math.sin(ang + 0.4)}`} fill={color} />
            )}
          </g>
        );
      }
      case "cloud":
        return (
          <g key={m.id} {...common} opacity={dim} data-installed={installed ? "1" : undefined}>
            <path d={cloudPath(g.points || (g.rect ? [[g.rect[0], g.rect[1]], [g.rect[2], g.rect[1]], [g.rect[2], g.rect[3]], [g.rect[0], g.rect[3]]] : []))}
                  fill={installed ? "rgba(21,128,61,0.10)" : hot ? "rgba(249,115,22,0.15)" : "none"}
                  stroke={installed ? "#15803d" : sel ? "#111" : color} strokeWidth={installed ? w * 2 : w} vectorEffect="non-scaling-stroke" />
            {installed && <DoneMark x={center(g)[0]} y={center(g)[1]} k={k} spin={spin} />}
            {sel && <Handles g={g} k={k} />}
          </g>
        );
      case "polygon":
      case "polyline": {
        // A takeoff (Bluebeam PolyLength / area) keeps its vertices and carries its measured
        // length or area as a label — it is never redrawn as its bounding box.
        const text = m.scale_values?.text ? String(m.scale_values.text).split(/[\r\n]+/).filter(Boolean) : [];
        const [lx, ly] = center(g);
        const ms = m.mark_state;
        const isBox = m.type === "polygon";
        const shown = isBox ? grow(g.points || []) : (g.points || []);
        const box = bboxOf({ points: shown });
        const lineW = Math.max(w, 2.5);
        const hitW = Math.max(12, lineW * 4, 14 * k);           // §3 — wider than the stroke, in document units
        const boxStroke = ms?.state === "installed" ? "#15803d" : sel || hot ? "#111" : color;
        // the X / partial badge / unlinked ring are drawn once, centrally, for every geometry
        return (
          <g key={m.id} {...common} opacity={dim} data-takeoff={text.length ? "1" : undefined}
             data-mark-state={ms?.state || undefined}>
            {isBox
              ? <polygon points={pts(shown)} fill={color} fillOpacity={ms?.state === "installed" ? 0.06 : m.symbol?.fill ? 0.3 : hot ? 0.22 : 0.10}
                         stroke={boxStroke} strokeWidth={Math.max(w, 1.8)} vectorEffect="non-scaling-stroke" pointerEvents="all"
                         strokeDasharray={m.symbol?.unmapped || m.session_live ? "4 3" : undefined} data-session={m.session_live ? "1" : undefined} data-unmapped={m.symbol?.unmapped ? "1" : undefined} />
              : <polyline points={pts(shown)} fill="none" stroke={sel ? "#111" : color} strokeWidth={lineW} strokeLinejoin="round" strokeLinecap="round" vectorEffect="non-scaling-stroke" />}
            {!isBox && <polyline points={pts(shown)} fill="none" stroke="transparent" strokeWidth={hitW} strokeLinejoin="round" strokeLinecap="round" pointerEvents="stroke" data-testid={`hit-${m.id}`} />}
            {text.length > 0 && (
              <g transform={`translate(${lx} ${ly}) scale(${k}) rotate(${-spin})`} pointerEvents="none">
                <text textAnchor="middle" fontSize="8" fill={color} fontWeight="600">{text[text.length - 1]}</text>
              </g>
            )}
            {m.origin === "app" && m.takeoff && (
              <circle cx={box[2]} cy={box[1]} r={2.2 * k} fill="#0284c7" stroke="white" strokeWidth={0.6 * k} pointerEvents="none" data-testid="origin-dot" />
            )}
            {m.origin === "ingest" && m.moved_in_app && (
              <circle cx={box[2]} cy={box[1]} r={2.2 * k} fill={m.review_on_reimport ? "#f59e0b" : "#64748b"} stroke="white" strokeWidth={0.6 * k} pointerEvents="none" data-testid="moved-dot">
                <title>Moved in the app{m.review_on_reimport ? " — review after re-import" : ""}</title>
              </circle>
            )}
            {ms?.state === "unassigned" && (
              <g transform={`translate(${lx} ${box[1] - 6 * k}) scale(${k}) rotate(${-spin})`} pointerEvents="none" data-testid="unassigned-badge">
                <rect x="-16" y="-7" width="32" height="12" rx="6" fill="#f59e0b" /><text x="0" y="2.5" textAnchor="middle" fontSize="7" fontWeight="700" fill="white">NO ROOM</text>
              </g>
            )}
            <title>{[m.subject, m.label, ...text, ms?.text].filter(Boolean).join(" · ")}</title>
          </g>
        );
      }
      case "rectangle":
      case "ellipse":
      case "measurement": {
        const r = g.rect || [0, 0, 0, 0];
        const stroke = installed ? "#15803d" : sel ? "#111" : color;
        return (
          <g key={m.id} {...common} opacity={dim} data-installed={installed ? "1" : undefined}>
            {m.type === "ellipse"
              ? <ellipse cx={(r[0] + r[2]) / 2} cy={(r[1] + r[3]) / 2} rx={(r[2] - r[0]) / 2} ry={(r[3] - r[1]) / 2} fill={installed ? "rgba(21,128,61,0.10)" : "none"} stroke={stroke} strokeWidth={installed ? w * 2 : w} vectorEffect="non-scaling-stroke" />
              : <rect x={r[0]} y={r[1]} width={r[2] - r[0]} height={r[3] - r[1]} fill={installed ? "rgba(21,128,61,0.10)" : "none"} stroke={stroke} strokeWidth={installed ? w * 2 : w} vectorEffect="non-scaling-stroke" />}
            {installed && <DoneMark x={(r[0] + r[2]) / 2} y={(r[1] + r[3]) / 2} k={k} spin={spin} />}
            {sel && <Handles g={g} k={k} />}
            {m.type === "measurement" && m.scale_values?.text && (
              <g transform={`translate(${(r[0] + r[2]) / 2} ${r[1]}) scale(${k}) rotate(${-spin})`} pointerEvents="none">
                <text textAnchor="middle" y="-3" fontSize="9" fill={color}>{m.scale_values.text}</text>
              </g>
            )}
          </g>
        );
      }
      case "text":
      case "callout": {
        const r = g.rect || [g.x, g.y, g.x + 120, g.y + 24];
        return (
          <g key={m.id} {...common} opacity={dim}>
            <rect x={r[0]} y={r[1]} width={Math.max(r[2] - r[0], 40)} height={Math.max(r[3] - r[1], 16)} fill="rgba(255,255,255,0.85)" stroke={sel ? "#111" : color} strokeWidth={1} vectorEffect="non-scaling-stroke" />
            <text x={r[0] + 3} y={r[1] + 11 * k} fontSize={10 * k} fill="#111">{(m.comments || m.label || "").slice(0, 60)}</text>
          </g>
        );
      }
      case "count":
      case "symbol":
      case "stamp": {
        const [cx, cy] = center(g);
        const isSymbol = m.type !== "stamp";
        const fill = isSymbol ? color : color;
        const rad = (isSymbol ? 8 : 10) * k;
        const label = isSymbol ? (m.product_code || m.subject || "").slice(0, 7) : (m.subject || "").slice(0, 14);
        // DRW-FIX-2 — the "Installed" stamp is a real placed markup, not an artifact: it now wears
        // the FT-1 checkmark and says who put it there on hover / tap.
        const installedStamp = !isSymbol && /^installed$/i.test(String(m.stamp || m.subject || "").trim());
        const who = [m.author, (m.created_at || "").slice(0, 10)].filter(Boolean).join(" · ");
        const ms = m.mark_state;
        const sbox = [cx - rad * 1.1, cy - rad * 1.1, cx + rad * 1.1, cy + rad * 1.1];
        return (
          <g key={m.id} {...common} opacity={dim} data-mark-state={ms?.state}
             data-stamp={!isSymbol ? (m.stamp || m.subject || "") : undefined}>
            {isSymbol ? (
              <>
                <circle cx={cx} cy={cy} r={rad} fill={fill} fillOpacity={0.85} stroke={sel || hot ? "#111" : "white"} strokeWidth={(sel || hot ? 2.5 : 1.2) * k} />
                <g transform={`translate(${cx} ${cy}) scale(${k}) rotate(${-spin})`} pointerEvents="none">
                  <text textAnchor="middle" y="16" fontSize="7" fill="#111" fontWeight="600">{label}</text>
                </g>
                {ms?.state === "installed" && <XMark box={sbox} k={k} />}
              </>
            ) : installedStamp ? (
              <g transform={`translate(${cx} ${cy}) scale(${k}) rotate(${-spin})`} data-testid="installed-stamp">
                <rect x="-44" y="-11" width="88" height="22" rx="11" fill="#15803d" stroke={sel ? "#111" : "white"} strokeWidth="2" />
                <path d="M-33 0 L-29 4.5 L-22 -4" fill="none" stroke="white" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                <text x="8" y="3.5" textAnchor="middle" fontSize="8.5" fontWeight="700" fill="white">INSTALLED</text>
              </g>
            ) : (
              <g transform={`translate(${cx} ${cy}) scale(${k}) rotate(${-spin})`}>
                <rect x="-34" y="-9" width="68" height="18" rx="3" fill="white" stroke={sel ? "#111" : color} strokeWidth="2" />
                <text x="0" y="4" textAnchor="middle" fontSize="8.5" fontWeight="700" fill={color}>{label.toUpperCase()}</text>
                {m.status === "resolved" && <line x1="-34" y1="0" x2="34" y2="0" stroke={color} strokeWidth="2" />}
              </g>
            )}
            {verify && <circle cx={cx} cy={cy} r={rad + 5 * k} fill="none" stroke="#f59e0b" strokeDasharray="3 2" strokeWidth={1.5 * k} />}
            {!isSymbol && <title>{`${m.subject || m.stamp || "Stamp"} stamp${who ? ` · ${who}` : ""} — tap for details`}</title>}
          </g>
        );
      }
      case "sticky":
        return <Pin key={m.id} x={g.x} y={g.y} k={k} spin={spin} color={verify ? "#f59e0b" : color} icon="…" selected={sel}
                    onClick={common.onClick} testId={`markup-${m.id}`} />;
      case "photo_pin":
        return <Pin key={m.id} x={g.x} y={g.y} k={k} spin={spin} color="#0f766e" icon="📷" selected={sel} onClick={common.onClick} testId={`markup-${m.id}`} />;
      case "punch_pin":
        return <Pin key={m.id} x={g.x} y={g.y} k={k} spin={spin} color={m.status === "resolved" ? "#6b7280" : "#dc2626"} label={String(m.punch_no || m.label || "")}
                    selected={sel} onClick={common.onClick} testId={`markup-${m.id}`} />;
      default:
        if (g.rect) {
          const r = g.rect;
          return <rect key={m.id} {...common} x={r[0]} y={r[1]} width={r[2] - r[0]} height={r[3] - r[1]} fill="none" stroke={color} strokeWidth={1} vectorEffect="non-scaling-stroke" opacity={dim} />;
        }
        return null;
    }
}

export default function MarkupOverlay({ markups, spaces, visible, selectedId, scale, spin = 0, onSelect, onSpaceTap,
                                        highlightIds, tappedIds, draft, showSpaces = true, roomStates = {}, refs = [], onRef }) {
  const k = 1 / (scale || 1);
  const hl = highlightIds || new Set();
  return (
    <>
      {showSpaces && (spaces || []).map((sp) => {
        const matched = !!sp.frs_room_ref?.room_number;
        const c = center(sp.geometry);
        const rs = roomStates[sp.id];
        const box = bboxOf(sp.geometry || {});
        const partial = rs && !rs.complete && rs.installed > 0;
        return (
          <g key={sp.id} onClick={(e) => { e.stopPropagation(); onSpaceTap?.(sp); }} style={{ cursor: "pointer" }}
             data-testid={`space-${sp.name}`} data-room-state={rs ? (rs.complete ? "complete" : partial ? "partial" : "open") : undefined}>
            <polygon points={pts(sp.geometry.points)} fill={matched ? "rgba(37,99,235,0.04)" : "rgba(245,158,11,0.10)"}
                     stroke={rs?.complete ? "#15803d" : matched ? "rgba(37,99,235,0.45)" : "#f59e0b"} strokeDasharray="6 4"
                     strokeWidth={rs?.complete ? 2 : 1.2} vectorEffect="non-scaling-stroke" pointerEvents="all" />
            {rs?.complete && <XMark box={box} k={k} />}
            {partial && <HalfFill box={box} k={k} spin={spin} text={`${rs.items_done} of ${rs.items} complete`} opacity={0.08} corner />}
            <g transform={`translate(${c[0]} ${c[1]}) scale(${k}) rotate(${-spin})`} pointerEvents="none">
              <text textAnchor="middle" fontSize="11" fill={matched ? "#1d4ed8" : "#b45309"}
                    fontWeight="600" opacity="0.8">{sp.frs_room_ref?.room_number || sp.name}{matched ? "" : " ?"}</text>
            </g>
          </g>
        );
      })}
      {/* DRW-5 §2 — printed sheet callouts resolved against the verified numbers; ≥44px targets */}
      {(refs || []).map((r, i) => {
        const [x0, y0, x1, y1] = r.rect;
        const minW = 44 * k; const minH = 44 * k;
        const w = Math.max(x1 - x0, minW); const h = Math.max(y1 - y0, minH);
        const cx = (x0 + x1) / 2; const cy = (y0 + y1) / 2;
        return (
          <g key={`ref-${i}`} onClick={(e) => { e.stopPropagation(); onRef?.(r); }} style={{ cursor: "pointer" }}
             data-testid={`sheet-ref-${r.sheet_number}-${i}`} data-ref-target={r.sheet_number}>
            <rect x={cx - w / 2} y={cy - h / 2} width={w} height={h} fill="transparent" pointerEvents="all" />
            <rect x={x0 - 2} y={y0 - 2} width={x1 - x0 + 4} height={y1 - y0 + 4} rx={2} fill="rgba(29,78,216,0.10)" stroke="#1d4ed8" strokeWidth={1} vectorEffect="non-scaling-stroke" pointerEvents="none" />
            <title>{`Go to ${r.sheet_number}${r.title ? ` — ${r.title}` : ""}`}</title>
          </g>
        );
      })}
      {(markups || []).filter((m) => visible(m)).map((m) => {
        let el = renderMarkup(m, { k, spin, hl, selectedId, onSelect });
        // PLAN-COMPLETE §8 — X'S AND PARTIAL BADGES, BOTH DIRECTIONS. A mark shows the state of
        // the FRS record it is linked to, wherever that completion was entered. A partial record
        // badges EVERY mark linked to it with the record's own figures — it never X's some of the
        // points, because nothing says which of ten lockers went in.
        const ms = m.mark_state;
        if (el && ms && ms.state !== "open") {
          const sbox = bboxOf(m.geometry || {});
          el = (
            <g key={m.id} data-mark-state={ms.state} data-mark-text={ms.text || undefined}>
              {el}
              {ms.state === "installed" && <XMark box={sbox} k={k} />}
              {ms.state === "partial" && <HalfFill box={sbox} k={k} spin={spin} text={ms.text} corner />}
              {ms.state === "unlinked" && <UnlinkedGlyph box={sbox} k={k} spin={spin} />}
            </g>
          );
        }
        if (el && (tappedIds || new Set()).has(m.id)) {
          el = <g key={m.id} data-tapped="1" data-testid={`tapped-${m.id}`}>{el}<TapMark box={bboxOf(m.geometry || {})} k={k} /></g>;
        }
        if (!el || !(m.pending || m.pending_edit)) return el;
        const [cx, cy] = center(m.geometry || {});
        return (
          <g key={m.id} opacity={m.conflict ? 1 : 0.8} data-testid={`pending-markup-${m.id}`} data-conflict={m.conflict ? "1" : undefined}>
            {el}
            <g transform={`translate(${cx} ${cy - 22 * k}) scale(${k}) rotate(${-spin})`} pointerEvents="none">
              <rect x="-22" y="-8" width="44" height="14" rx="7" fill={m.conflict ? "#dc2626" : "#f59e0b"} />
              <text x="0" y="3" textAnchor="middle" fontSize="8" fontWeight="700" fill="white">{m.conflict ? "REVIEW" : "SYNC"}</text>
            </g>
          </g>
        );
      })}
      {draft && (
        <g pointerEvents="none">
          {draft.type === "pen" || draft.type === "highlighter"
            ? <polyline points={pts(draft.points)} fill="none" stroke={draft.color} strokeWidth={draft.type === "highlighter" ? 10 : draft.width}
                        strokeOpacity={draft.type === "highlighter" ? 0.4 : 1} strokeLinecap="round" vectorEffect={draft.type === "highlighter" ? undefined : "non-scaling-stroke"} />
            : draft.type === "cloud" && draft.points?.length === 2
              ? <path d={cloudPath([[draft.points[0][0], draft.points[0][1]], [draft.points[1][0], draft.points[0][1]], [draft.points[1][0], draft.points[1][1]], [draft.points[0][0], draft.points[1][1]]])} fill="none" stroke={draft.color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
              : draft.type === "square" && draft.points?.length === 2
                ? <rect x={Math.min(draft.points[0][0], draft.points[1][0])} y={Math.min(draft.points[0][1], draft.points[1][1])}
                        width={Math.abs(draft.points[1][0] - draft.points[0][0])} height={Math.abs(draft.points[1][1] - draft.points[0][1])}
                        fill="none" stroke={draft.color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                : draft.type === "circle" && draft.points?.length === 2
                  ? <ellipse cx={(draft.points[0][0] + draft.points[1][0]) / 2} cy={(draft.points[0][1] + draft.points[1][1]) / 2}
                             rx={Math.abs(draft.points[1][0] - draft.points[0][0]) / 2} ry={Math.abs(draft.points[1][1] - draft.points[0][1]) / 2}
                             fill="none" stroke={draft.color} strokeWidth={1.5} vectorEffect="non-scaling-stroke" />
                  : draft.type === "arrow" && draft.points?.length === 2
                    ? <line x1={draft.points[0][0]} y1={draft.points[0][1]} x2={draft.points[1][0]} y2={draft.points[1][1]} stroke={draft.color} strokeWidth={2} vectorEffect="non-scaling-stroke" />
                    : null}
        </g>
      )}
    </>
  );
}
